
import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { ProductMegaMenu } from './ProductMegaMenu';
import { HomepageMegaMenu } from './HomepageMegaMenu';
import { AdminMegaMenu } from './AdminMegaMenu';
import { Button } from '@/components/ui/button';
import { preloadComponent } from '@/utils/codeSplitting';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  label: string;
  href: string;
  hasDropdown?: boolean;
  hasMegaMenu?: boolean;
  dropdownItems?: { label: string; href: string }[];
  variant?: 'primary' | 'secondary' | 'outline';
  adminOnly?: boolean;
}

interface DesktopNavigationProps {
  navItems: NavItem[];
  isActive: (href: string) => boolean;
  megaMenuOpen: string;
  onMouseEnterNavItem: (itemLabel: string) => void;
  onMouseLeaveNavItem: () => void;
  onMouseEnterMegaMenuContent: () => void;
  onMouseLeaveMegaMenuContent: () => void;
  onCloseMegaMenu: () => void;
  onNavigationClick?: (href: string) => void;
  onUserActivity?: () => void;
  overlayTop: number;
}

export const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  navItems,
  isActive,
  megaMenuOpen,
  onMouseEnterNavItem,
  onMouseLeaveNavItem,
  onMouseEnterMegaMenuContent,
  onMouseLeaveMegaMenuContent,
  onCloseMegaMenu,
  onNavigationClick,
  onUserActivity,
  overlayTop
}) => {
  const { hasAdminAccess } = useAuth();
  // Updated: Mega menu mouse events for instant close
  const handleMegaMenuEnter = () => {
    onMouseEnterMegaMenuContent();
  };
  const handleMegaMenuLeave = () => {
    onMouseLeaveMegaMenuContent();
  };

  // Refs for click-outside detection when using portal
  const navContainerRef = React.useRef<HTMLDivElement | null>(null);
  const portalRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        portalRef.current && !portalRef.current.contains(target) &&
        navContainerRef.current && !navContainerRef.current.contains(target)
      ) {
        onCloseMegaMenu();
      }
    };
    if (megaMenuOpen) {
      document.addEventListener('mousedown', handleDocClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, [megaMenuOpen, onCloseMegaMenu]);

  // Prefetch route chunks on hover for faster nav
  const prefetchRoute = (href: string) => {
    switch (href) {
      case '/portfolio':
        return preloadComponent(() => import('@/pages/Portfolio'));
      case '/blog':
        return preloadComponent(() => import('@/pages/Blog'));
      case '/careers':
        return preloadComponent(() => import('@/pages/Careers'));
      case '/faq':
        return preloadComponent(() => import('@/pages/FAQ'));
      case '/client-login':
        return preloadComponent(() => import('@/pages/ClientLogin'));
      case '/price-beat-guarantee':
        return preloadComponent(() => import('@/pages/PriceBeatGuarantee'));
      case '/website-wizard':
        return preloadComponent(() => import('@/pages/WebsiteWizard'));
      case '/checkmysite':
        return preloadComponent(() => import('@/pages/CheckMySite'));
      case '/analytics':
        return preloadComponent(() => import('@/pages/Analytics'));
      case '/privacy':
        return preloadComponent(() => import('@/pages/Privacy'));
      case '/legal':
        return preloadComponent(() => import('@/pages/Legal'));
      case '/cookie-policy':
        return preloadComponent(() => import('@/pages/CookiePolicy'));
      case '/gdpr-compliance':
        return preloadComponent(() => import('@/pages/GdprCompliance'));
      case '/profile':
      case '/my-profile':
        return preloadComponent(() => import('@/pages/Profile'));
      default:
        return;
    }
  };

  const handleClick = (href: string, e: React.MouseEvent, item: NavItem) => {
    if (item.hasMegaMenu) {
      // Tablet behavior: first click opens mega menu, second click navigates
      if (navigator.maxTouchPoints > 0) {
        if (megaMenuOpen === item.label) {
          // Second click: navigate to page
          onCloseMegaMenu();
          if (onNavigationClick) {
            e.preventDefault();
            onNavigationClick(href);
          }
        } else {
          // First click: open mega menu
          e.preventDefault();
          onMouseEnterNavItem(item.label);
        }
        return;
      } else {
        // Desktop behavior: click navigates directly
        onCloseMegaMenu();
        if (onNavigationClick) {
          e.preventDefault();
          onNavigationClick(href);
        }
        return;
      }
    }
    // For regular links, allow default React Router Link navigation
  };

  const handleKeyDown = (item: NavItem, event: React.KeyboardEvent) => {
    if (item.hasDropdown || item.hasMegaMenu) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (item.hasMegaMenu) {
          if (megaMenuOpen === item.label) {
            onCloseMegaMenu();
          } else {
            onMouseEnterNavItem(item.label);
          }
        }
      }
      // Let Enter/Space activate the link normally for navigation
      if (event.key === 'Escape' && item.hasMegaMenu) {
        onCloseMegaMenu();
      }
    }
  };

  const renderMegaMenu = (itemLabel: string) => {
    switch (itemLabel) {
      case 'Products':
        return <ProductMegaMenu onClose={onCloseMegaMenu} onNavigationClick={onNavigationClick} onUserActivity={onUserActivity} />;
      case 'Homepage':
        return <HomepageMegaMenu onClose={onCloseMegaMenu} onNavigationClick={onNavigationClick} onUserActivity={onUserActivity} />;
      case 'Admin Portal':
        return hasAdminAccess() ? <AdminMegaMenu onClose={onCloseMegaMenu} onNavigationClick={onNavigationClick} onUserActivity={onUserActivity} /> : null;
      default:
        return null;
    }
  };

  return (
    <div ref={navContainerRef} className="hidden lg:flex items-center space-x-5 xl:space-x-8 pr-2 xl:pr-4">
      {/* Note: no role="menubar" — this is a site nav of links, not an app menu.
          role="menubar" would require role="menuitem" children (axe aria-required-children). */}
      {navItems
        .filter(item => !item.adminOnly || hasAdminAccess())
        .map((item, idx) => (
        <div
          key={item.label}
          className="relative group dropdown-container"
          onPointerEnter={() => {
            prefetchRoute(item.href);
            if (item.hasMegaMenu && navigator.maxTouchPoints === 0) {
              onMouseEnterNavItem(item.label);
            }
          }}
          onPointerLeave={() => {
            if (item.hasMegaMenu && navigator.maxTouchPoints === 0) {
              onMouseLeaveNavItem();
            }
          }}
          onClick={() => {
            if (item.hasMegaMenu && navigator.maxTouchPoints > 0) {
              if (megaMenuOpen === item.label) {
                onCloseMegaMenu();
              } else {
                onMouseEnterNavItem(item.label);
              }
            }
          }}
        >
          {item.variant === 'primary' ? (
            <Link to={item.href} onClick={(e) => handleClick(item.href, e, item)}>
              <Button
                className="bg-gradient-to-r from-green-700 to-emerald-700 hover:from-green-800 hover:to-emerald-800 text-white font-semibold transition-all duration-300 hover:shadow-lg border-0 rounded-md text-sm px-4 py-2"
                size="default"
              >
                {item.label}
              </Button>
            </Link>
          ) : (
            <Link
              to={item.href}
              onClick={(e) => handleClick(item.href, e, item)}
              className={`flex min-h-11 items-center whitespace-nowrap px-3 py-2 text-sm font-medium transition-colours ${
                isActive(item.href)
                  ? 'text-primary bg-primary/10'
                  : 'text-foreground hover:text-primary hover:bg-accent'
              } ${
                item.adminOnly ? 'relative' : ''
              }`}
              role="menuitem"
              aria-current={isActive(item.href) ? "page" : undefined}
              aria-haspopup={item.hasMegaMenu ? "menu" : undefined}
              aria-expanded={item.hasMegaMenu ? (megaMenuOpen === item.label) : undefined}
              tabIndex={0}
              onKeyDown={event => handleKeyDown(item, event)}
            >
              {item.label}
              {item.adminOnly && (
                <span className="ml-1 text-xs">🛡️</span>
              )}
              {item.hasMegaMenu && (
                <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
              )}
            </Link>
          )}
        </div>
      ))}

      {/* Mega Menus via portal - positioned directly under nav */}
      {megaMenuOpen && createPortal(
        (
          <div
            ref={portalRef}
            className="left-0 right-0 px-4 z-[160] pointer-events-auto"
            style={{ position: 'fixed', top: overlayTop, width: '100%' }}
            onPointerEnter={navigator.maxTouchPoints === 0 ? handleMegaMenuEnter : undefined}
            onPointerLeave={navigator.maxTouchPoints === 0 ? handleMegaMenuLeave : undefined}
            tabIndex={0}
            role="menu"
            aria-label={`${megaMenuOpen} mega menu`}
          >
            <div className="max-w-5xl mx-auto">
              {renderMegaMenu(megaMenuOpen)}
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};
